import React, { useState, useEffect, useRef } from 'react';
import * as THREE from 'three';

// 飘落的文学意象字符
const POETIC_CHARS = [
  "星", "月", "心", "美", "你", "我", "光", "爱", "树", "下", "见", "遇", "风", "叶", "晚", "空"
];

const App = () => {
 // 在 App.js 的 return 之前，或者 CSS 部分添加
const textStyle = {
  color: '#ffffff', // 强制文字变成纯白色
  textShadow: '0 0 10px rgba(255,255,255,0.8)', // 给文字加一层发光的白雾
  textAlign: 'center',
  zIndex: 100
};
  const mountRef = useRef(null);
  const [stage, setStage] = useState('welcome'); 
  const [answers, setAnswers] = useState([]);
  const [currentInput, setCurrentInput] = useState("");
  const [aiPoem, setAiPoem] = useState(null);
  const [cursorPos, setCursorPos] = useState({ x: -100, y: -100 });
  const [isDown, setIsDown] = useState(false);
  const [snowflakes, setSnowflakes] = useState([]);
  
  const sceneRef = useRef({
    scene: null, 
    camera: null, 
    renderer: null, 
    treeParticles: null, 
    colorAttr: null,
    snowParticles: null,
    goldParticles: null,
    ornaments: null, 
    clock: new THREE.Clock()
  });

  const requestRef = useRef();

  // 处理 UI 层的字符飘落逻辑
  useEffect(() => {
    const timer = setInterval(() => {
      setStage(currentStage => {
        const isWriting = currentStage === 'writing';
        if (isWriting) {
          setSnowflakes(prev => [
            ...prev.slice(-40), 
            {
              id: `${Math.random()}-${Date.now()}`,
              char: POETIC_CHARS[Math.floor(Math.random() * POETIC_CHARS.length)],
              left: Math.random() * 100,
              duration: 10 + Math.random() * 5, 
              delay: Math.random() * 0.2,
              size: 14 + Math.random() * 10,
              opacity: 0.2 + Math.random() * 0.3,
              swing: 40 + Math.random() * 40 
            }
          ]);
        } else {
          setSnowflakes(prev => prev.length > 0 ? prev.slice(8) : []);
        }
        return currentStage;
      });
    }, 400); 
    return () => clearInterval(timer);
  }, []);

  // Three.js 核心渲染逻辑
  useEffect(() => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, w / h, 0.1, 4000);
    camera.position.set(0, 100, 850); 

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    if (mountRef.current) {
      mountRef.current.appendChild(renderer.domElement);
    }

    // --- 构建圣诞树粒子系统 ---
    const treePoints = [];
    const treeColors = [];
    const layers = 11; 
    
    // 树尖和顶端随机粒子
    for (let i = 0; i < 450; i++) {
      const y = Math.random() * 450 - 200;
      if (y < -180) continue; 
      const r = (1 - (y + 200) / 450) * 4 + 1.5;
      const angle = Math.random() * Math.PI * 2;
      treePoints.push(Math.cos(angle) * r, y, Math.sin(angle) * r);
      treeColors.push(0.9, 0.9, 0.9);
    }
    
    // 树身层叠粒子
    for (let l = 0; l < layers; l++) {
      const layerY = -150 + (l * 48); 
      const layerRadius = (layers - l) * 32;
      const count = (layers - l) * 130;
      for (let i = 0; i < count; i++) {
        const angle = Math.random() * Math.PI * 2;
        const dist = Math.pow(Math.random(), 0.75) * layerRadius;
        const bend = Math.pow(dist / layerRadius, 2.2) * 38; 
        treePoints.push(Math.cos(angle) * dist, layerY - bend, Math.sin(angle) * dist);
        treeColors.push(0.9, 0.9, 0.9); 
      }
    }
    
    const treeGeo = new THREE.BufferGeometry();
    treeGeo.setAttribute('position', new THREE.Float32BufferAttribute(treePoints, 3));
    const colorAttr = new THREE.Float32BufferAttribute(treeColors, 3);
    treeGeo.setAttribute('color', colorAttr);
    
    const treeParticles = new THREE.Points(treeGeo, new THREE.PointsMaterial({ 
      size: 4.0, 
      vertexColors: true, 
      transparent: true, 
      opacity: 0.95, 
      blending: THREE.AdditiveBlending 
    }));
    treeParticles.position.y = -50;
    scene.add(treeParticles);

    // --- 构建装饰球和灯带 ---
    const ornPoints = [];
    const ornColors = [];
    // 螺旋灯带
    for(let i=0; i<300; i++){
        const t = i / 300;
        const y = -150 + t * 450;
        const r = (1 - (y + 150) / 500) * 320;
        const angle = t * Math.PI * 12; 
        ornPoints.push(Math.cos(angle) * r, y, Math.sin(angle) * r);
        ornColors.push(1, 1, 0.6); 
    }
    // 随机点缀装饰球
    const ballColors = [[0.8, 0.1, 0.1], [0.1, 0.3, 0.8], [0.9, 0.9, 0.9]];
    for(let i=0; i<60; i++){
        const y = Math.random() * 400 - 150;
        const r = (1 - (y + 150) / 550) * 300 * Math.random();
        const angle = Math.random() * Math.PI * 2;
        ornPoints.push(Math.cos(angle) * r, y, Math.sin(angle) * r);
        const c = ballColors[Math.floor(Math.random()*ballColors.length)];
        ornColors.push(c[0], c[1], c[2]);
    }

    const ornGeo = new THREE.BufferGeometry();
    ornGeo.setAttribute('position', new THREE.Float32BufferAttribute(ornPoints, 3));
    ornGeo.setAttribute('color', new THREE.Float32BufferAttribute(ornColors, 3));
    const ornaments = new THREE.Points(ornGeo, new THREE.PointsMaterial({
        size: 7.0,
        vertexColors: true,
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending
    }));
    ornaments.position.y = -50;
    scene.add(ornaments);

    // --- 背景雪花系统 ---
    const snowCount = 2000;
    const snowPositions = new Float32Array(snowCount * 3);
    const snowVelocities = new Float32Array(snowCount);
    for (let i = 0; i < snowCount; i++) {
      snowPositions[i * 3] = (Math.random() - 0.5) * 2000;
      snowPositions[i * 3 + 1] = Math.random() * 1200 - 400;
      snowPositions[i * 3 + 2] = (Math.random() - 0.5) * 2000;
      snowVelocities[i] = 0.5 + Math.random() * 1.5;
    }
    const snowGeo = new THREE.BufferGeometry();
    snowGeo.setAttribute('position', new THREE.BufferAttribute(snowPositions, 3));
    const snowMat = new THREE.PointsMaterial({ size: 1.5, color: 0xffffff, transparent: true, opacity: 0.4, blending: THREE.AdditiveBlending });
    const snowParticles = new THREE.Points(snowGeo, snowMat);
    scene.add(snowParticles);

    // --- 结局金色尘埃系统 ---
    const goldCount = 1800;
    const goldPositions = new Float32Array(goldCount * 3);
    const goldSpeeds = new Float32Array(goldCount);
    for (let i = 0; i < goldCount; i++) {
      goldPositions[i * 3] = (Math.random() - 0.5) * 2500;
      goldPositions[i * 3 + 1] = Math.random() * 1500 - 750;
      goldPositions[i * 3 + 2] = (Math.random() - 0.5) * 2500;
      goldSpeeds[i] = 0.2 + Math.random() * 0.4;
    }
    const goldGeo = new THREE.BufferGeometry();
    goldGeo.setAttribute('position', new THREE.BufferAttribute(goldPositions, 3));
    const goldMat = new THREE.PointsMaterial({ size: 2.5, color: 0xffd700, transparent: true, opacity: 0, blending: THREE.AdditiveBlending });
    const goldParticles = new THREE.Points(goldGeo, goldMat);
    scene.add(goldParticles);

    sceneRef.current = { scene, camera, renderer, treeParticles, colorAttr, snowParticles, goldParticles, ornaments, clock: new THREE.Clock() };

    let colorLerp = 0;
    let bgLightLerp = 0;

    // 每一帧的渲染逻辑
    const animate = () => {
      const { renderer, scene, camera, treeParticles, colorAttr, snowParticles, goldParticles, ornaments } = sceneRef.current;
      if (!renderer || !scene || !camera) return;

      const isWriting = stage === 'writing';
      const isResult = stage === 'result';

      // 状态平滑过渡系数
      colorLerp = THREE.MathUtils.lerp(colorLerp, isResult ? 1 : 0.05, 0.012);
      bgLightLerp = THREE.MathUtils.lerp(bgLightLerp, isResult ? 1 : 0, 0.008);
      
      // 动态背景色
      const bgR = 0.01 + 0.01 * bgLightLerp; 
      const bgG = 0.015 + 0.015 * bgLightLerp;
      const bgB = 0.02 + 0.04 * bgLightLerp;
      renderer.setClearColor(new THREE.Color(bgR, bgG, bgB), 1);

      // 雪花下落动画
      if (snowParticles) {
        const targetSnowOpacity = isResult ? 0 : (isWriting ? 0.2 : 0.4);
        snowParticles.material.opacity = THREE.MathUtils.lerp(snowParticles.material.opacity, targetSnowOpacity, 0.03);
        const pos = snowParticles.geometry.attributes.position.array;
        for (let i = 0; i < snowCount; i++) {
          pos[i * 3 + 1] -= snowVelocities[i];
          if (pos[i * 3 + 1] < -500) pos[i * 3 + 1] = 700;
        }
        snowParticles.geometry.attributes.position.needsUpdate = true;
      }

      // 金粉上升动画
      if (goldParticles) {
        const targetGoldOpacity = isResult ? 0.85 : 0;
        goldParticles.material.opacity = THREE.MathUtils.lerp(goldParticles.material.opacity, targetGoldOpacity, 0.01);
        const gPos = goldParticles.geometry.attributes.position.array;
        for (let i = 0; i < goldCount; i++) {
          gPos[i * 3 + 1] += goldSpeeds[i]; 
          if (gPos[i * 3 + 1] > 750) gPos[i * 3 + 1] = -750;
        }
        goldParticles.geometry.attributes.position.needsUpdate = true;
      }

      // 装饰物闪烁与显现
      if (ornaments) {
          const targetOrnOpacity = isResult ? (0.6 + Math.sin(Date.now()*0.002)*0.2) : 0;
          ornaments.material.opacity = THREE.MathUtils.lerp(ornaments.material.opacity, targetOrnOpacity, 0.02);
          ornaments.rotation.y = treeParticles.rotation.y;
      }

      // 树木颜色动态变换 (变金/变银)
      if (colorAttr) {
        const cols = colorAttr.array;
        const treePos = treeParticles.geometry.attributes.position.array;
        for (let i = 0; i < cols.length; i += 3) {
          const yPos = treePos[i+1];
          const goldenFactor = Math.pow(Math.max(0, (yPos + 80) / 410), 0.7) * colorLerp;
          if (i < 450 * 3) {
            cols[i] = THREE.MathUtils.lerp(0.85, 0.05, colorLerp);
            cols[i+1] = THREE.MathUtils.lerp(0.85, 0.05, colorLerp);
            cols[i+2] = THREE.MathUtils.lerp(0.85, 0.05, colorLerp);
          } else {
            const baseR = THREE.MathUtils.lerp(0.85, 0.1, colorLerp);
            const baseG = THREE.MathUtils.lerp(0.85, 0.12, colorLerp);
            const baseB = THREE.MathUtils.lerp(0.85, 0.18, colorLerp);
            cols[i] = THREE.MathUtils.lerp(baseR, 1.0, goldenFactor);
            cols[i+1] = THREE.MathUtils.lerp(baseG, 0.9, goldenFactor);
            cols[i+2] = THREE.MathUtils.lerp(baseB, 0.15, goldenFactor);
          }
        }
        colorAttr.needsUpdate = true;
      }

      treeParticles.rotation.y += isResult ? 0.0004 : 0.0012;
      renderer.render(scene, camera);
      requestRef.current = requestAnimationFrame(animate);
    };

    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    animate();

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(requestRef.current);
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
    };
  }, [stage]);

  // 调用 AI 接口生成诗词
  const generatePoem = async (finalAnswers) => {
    setStage('writing');
    const startTime = Date.now();
    const apiKey = "sk-u5vkgabfH8RpgryFumOyH4qycQtU9nuMr6eSlPS7a0vzFtZu";
    const systemPrompt = "你是一位极具灵魂厚度的诗人。请根据用户的感悟，创作一句极简、温柔且充满力量的现代诗（25-45字）。只返回诗句。";
    const userPrompt = finalAnswers.map((item, i) => `回答${i+1}: ${item.a}`).join('\n');
    
   try {
    const response = await fetch(`https://max.openai365.top/v1/chat/completions`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiKey}`, // 中转站通常需要 Bearer 认证
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        model: "gemini-3-pro-preview", // 使用你刚才选中的那个厉害名字
        messages: [
          { role: "system", content: systemPrompt },
          { role: "user", content: userPrompt }
        ],
        temperature: 0.7
      })
    });

    const result = await response.json();
    // 注意：OpenAI 格式的结果提取方式和 Google 不一样
    const poem = result.choices?.[0]?.message?.content || "即使在最深的冬夜，你的灵魂也自带光芒。";
      
      // 保证至少有 6.5 秒的沉浸等待时间
      const elapsed = Date.now() - startTime;
      const minWait = 6500;
      if (elapsed < minWait) await new Promise(r => setTimeout(r, minWait - elapsed));
      
      setAiPoem(poem);
      setStage('result');
    } catch (error) {
      await new Promise(r => setTimeout(r, 4000));
      setAiPoem("寒冬之后，春天总会如期而至。");
      setStage('result');
    }
  };

  return (
    <div className="relative w-full h-screen overflow-hidden bg-black select-none touch-none cursor-none font-serif text-white"
      onPointerMove={(e) => setCursorPos({ x: e.clientX, y: e.clientY })}
      onPointerDown={() => setIsDown(true)}
      onPointerUp={() => setIsDown(false)}>
      
      {/* 渲染画布的容器 */}
      <div ref={mountRef} className="absolute inset-0 z-0" />

      {/* 字符飘落视觉层 */}
      <div className="absolute inset-0 pointer-events-none z-10 overflow-hidden">
        {snowflakes.map(snow => (
          <div key={snow.id} className="absolute text-white/40 mix-blend-screen"
            style={{ 
              left: `${snow.left}%`, 
              top: `-80px`, 
              fontSize: `${snow.size}px`, 
              opacity: snow.opacity, 
              animation: `fallText ${snow.duration}s linear ${snow.delay}s forwards`, 
              '--swing': `${snow.swing}px` 
            }}>
            {snow.char}
          </div>
        ))}
      </div>

      <style>{`
        @keyframes fallText {
          0% { transform: translateY(0) translateX(0) rotate(-10deg); opacity: 0; }
          15% { opacity: 0.8; }
          100% { transform: translateY(110vh) translateX(var(--swing)) rotate(10deg); opacity: 0; }
        }
        .poem-particle {
          animation: floatPoem 10s ease-in-out infinite;
          background: linear-gradient(135deg, #fff 60%, #ffd700 100%);
          -webkit-background-clip: text;
          color: transparent;
          filter: drop-shadow(0 0 15px rgba(255,215,0,0.4));
        }
        @keyframes floatPoem {
          0%, 100% { transform: translateY(0) scale(1); text-shadow: 0 0 20px rgba(255,255,255,0.1); }
          50% { transform: translateY(-6px) scale(1.002); text-shadow: 0 0 40px rgba(255,215,0,0.3); }
        }
        .writing-mask {
          background: radial-gradient(circle at center, rgba(255,255,255,0.08) 0%, transparent 70%);
          backdrop-filter: blur(12px);
          box-shadow: 0 0 60px rgba(0,0,0,0.4);
          border: 1px solid rgba(255,255,255,0.15);
        }
        .input-mask {
          background: rgba(255, 255, 255, 0.04);
          backdrop-filter: blur(18px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: inset 0 0 20px rgba(255,255,255,0.02), 0 12px 40px rgba(0, 0, 0, 0.4);
          border-radius: 2.5rem;
        }
        .snow-dot {
          position: absolute;
          background: white;
          border-radius: 50%;
          filter: blur(1px);
          animation: snowFloat 3s linear infinite;
        }
        @keyframes snowFloat {
          0% { transform: translateY(-10px) translateX(0); opacity: 0; }
          50% { opacity: 0.6; }
          100% { transform: translateY(10px) translateX(10px); opacity: 0; }
        }
        .top-content {
          position: absolute;
          top: 15vh;
          width: 100%;
          left: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 0 2rem;
        }
        .inner-button {
          position: absolute;
          bottom: 1.5rem;
          right: 1.5rem;
          padding: 0.6rem 2rem;
          border: 1px solid rgba(255,255,255,0.25);
          background: rgba(255,255,255,0.05);
          backdrop-filter: blur(10px);
          border-radius: 9999px;
          color: rgba(255,255,255,0.8);
          font-size: 13px;
          letter-spacing: 0.8em;
          text-indent: 0.8em;
          transition: all 0.4s ease;
          z-index: 20;
        }
        .inner-button:hover:not(:disabled) {
          background: white;
          color: black;
          border-color: white;
        }
        .inner-button:disabled {
          opacity: 0.1;
        }
      `}</style>

      {/* 自定义准星光标 */}
      <div className="absolute pointer-events-none z-[110] transition-transform duration-150" style={{ left: cursorPos.x, top: cursorPos.y, transform: 'translate(-50%, -50%)' }}>
        <div className="relative flex items-center justify-center">
          <div className={`absolute w-10 h-[1px] bg-white transition-opacity duration-300 ${isDown ? 'opacity-10' : 'opacity-40'}`} />
          <div className={`absolute h-10 w-[1px] bg-white transition-opacity duration-300 ${isDown ? 'opacity-10' : 'opacity-40'}`} />
          {!isDown && <div className="w-1.5 h-1.5 bg-white rounded-full opacity-60 shadow-[0_0_15px_white]" />}
        </div>
      </div>

      <div className={`absolute inset-0 z-40 text-center pointer-events-none`}>
        
        {/* 欢迎界面 */}
        {stage === 'welcome' && (
          <div className="top-content" style={textStyle}>
            <h1 style={textStyle} className="text-white text-[20px] tracking-[1.2em] font-light opacity-80 mb-10 ml-[1.2em] drop-shadow-2xl">圣诞快乐</h1>
            <div className="relative flex items-center pointer-events-auto">
             <p style={textStyle} className="text-white text-[13px] tracking-[0.6em] font-extralight opacity-40 ml-[0.6em]">我想为你写一句诗</p>
              <button onClick={() => setStage('question1')} className="ml-10 p-4 border border-white/20 rounded-full hover:bg-white hover:text-black transition-all duration-700 cursor-none">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </button>
            </div>
          </div>
        )}

        {/* 选项界面 */}
        {stage === 'question1' && (
          <>
            <div className="top-content animate-in fade-in zoom-in-95 duration-1000"style={textStyle}>
               <h2 className="text-[19px] leading-[2] tracking-[0.35em] opacity-90 font-light max-w-xl">在这个漫长的冬夜里，你想停留在树下休息会吗？</h2>
            </div>
            <div className="absolute top-[50vh] left-0 w-full flex justify-between px-[10vw] md:px-[15vw] pointer-events-auto">
              <button 
                onClick={() => { setAnswers([{ q: "停留", a: "愿意" }]); setStage('question2'); }} 
                className="px-12 py-3.5 border border-white/30 tracking-[0.5em] text-[13px] hover:bg-white hover:text-black transition-all rounded-full min-w-[140px] animate-in slide-in-from-left-8 duration-1000"
              >
                愿意
              </button>
              <button 
                onClick={() => setStage('farewell')} 
                className="px-12 py-3.5 border border-white/10 tracking-[0.5em] text-[13px] opacity-30 hover:opacity-100 transition-all rounded-full min-w-[140px] animate-in slide-in-from-right-8 duration-1000"
              >
                拒绝
              </button>
            </div>
          </>
        )}

        {/* 输入界面 */}
        {(stage === 'question2' || stage === 'question3') && (
          <div className="top-content animate-in fade-in slide-in-from-bottom-8 duration-1000 pointer-events-auto"style={textStyle}>
            <h2 className="text-[18px] leading-[1.8] tracking-[0.25em] mb-12 opacity-85 font-light px-8 max-w-2xl">
              {stage === 'question2' ? "当时针拨回到最初的起点，你最想对那个在雪地里独自前行的自己说些什么？" : "在这个流动的宇宙中，是什么让你在这一刻，愿意与这棵孤独的树产生交集？"}
            </h2>
            <div className="relative w-full max-w-xl group">
                <div className="input-mask absolute inset-0 -m-3 transition-all duration-700 group-focus-within:bg-white/[0.07] group-focus-within:border-white/25" />
                <textarea 
                  value={currentInput} 
                  onChange={(e) => setCurrentInput(e.target.value)} 
                  placeholder="在此轻声倾诉..."
                  className="relative w-full bg-transparent p-8 outline-none resize-none text-center tracking-[0.2em] leading-[2] h-52 cursor-none transition-all placeholder:opacity-20 z-10 text-[15px]" 
                  autoFocus 
                />
                <button 
                  onClick={() => {
                    const newAnswers = [...answers, { q: stage, a: currentInput }];
                    setAnswers(newAnswers);
                    setCurrentInput("");
                    if (stage === 'question2') setStage('question3'); else generatePoem(newAnswers);
                  }} 
                  disabled={!currentInput.trim()} 
                  className="inner-button"
                >
                  倾诉
                </button>
            </div>
          </div>
        )}

        {/* 等待动画界面 */}
        {stage === 'writing' && (
          <div className="top-content flex flex-col items-center"style={textStyle}>
            <div className="writing-mask relative px-14 py-10 rounded-[2.5rem] overflow-hidden">
                <div className="snow-dot w-1 h-1 left-6 top-6" style={{ animationDelay: '0.2s' }} />
                <div className="snow-dot w-1.5 h-1.5 right-10 top-12" style={{ animationDelay: '0.8s' }} />
                <div className="snow-dot w-1 h-1 left-1/2 bottom-6" style={{ animationDelay: '1.4s' }} />
                <div className="relative z-10 tracking-[1.2em] text-[12px] opacity-70 text-white ml-[1.2em] animate-pulse">
                  思绪正在风雪中凝结成文字
                </div>
            </div>
          </div>
        )}

        {/* 结果显示界面 */}
        {stage === 'result' && (
          <div className="top-content flex flex-col items-center animate-in fade-in zoom-in-95 duration-[4000ms]"style={textStyle}>
            <div className="max-w-4xl w-full flex flex-col items-center pointer-events-auto">
              <p className="poem-particle text-[20px] md:text-[25px] leading-[2.8] tracking-[0.4em] font-light px-12 max-w-3xl">
                {aiPoem}
              </p>
              <button onClick={() => { setStage('welcome'); setAiPoem(null); setSnowflakes([]); setAnswers([]); }} 
                className="mt-12 text-[10px] tracking-[1.2em] opacity-40 hover:opacity-100 transition-all border border-white/10 hover:border-white/30 px-10 py-3 rounded-full cursor-none ml-[1.2em] backdrop-blur-sm">
                - 重寻片刻宁静 -
              </button>
            </div>
          </div>
        )}

        {/* 告别界面 */}
        {stage === 'farewell' && (
          <div className="absolute inset-0 flex flex-col items-center"style={textStyle}>
            <div className="top-content animate-in fade-in slide-in-from-top-4 duration-[1500ms]">
              <p className="text-[20px] tracking-[0.5em] font-light opacity-80 leading-[2.5] drop-shadow-lg">
                愿这片刻的沉默，<br/><span className="opacity-60">能伴你度过漫漫长夜。</span>
              </p>
            </div>
            
            <div className="absolute bottom-[20vh] pointer-events-auto animate-in fade-in zoom-in-95 duration-[2000ms]">
              <button onClick={() => setStage('welcome')} 
                className="px-16 py-3.5 tracking-[0.8em] text-[12px] border border-white/10 rounded-full cursor-none ml-[0.8em] text-white/70 hover:text-white">
                告别
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default App;
